import React from "react";
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function About() {
  return (
    <div>
      <Helmet>
        <title>About ILoveConvert — Free File Converter from Hosur, India</title>
        <meta name="description" content="ILoveConvert is a free online file converter built by Techb Software Solutions Pvt Ltd, Hosur. Convert PDF, Word, Excel and images instantly. No signup needed." />
        <link rel="canonical" href="https://iloveconvert.in/about" />
        <meta property="og:title" content="About ILoveConvert — Free File Converter from Hosur" />
        <meta property="og:url" content="https://iloveconvert.in/about" />
        <meta property="og:description" content="Learn about ILoveConvert — built by Techb Software Solutions Pvt Ltd, Hosur. Our mission is to make file conversion free and accessible for everyone." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About ILoveConvert — Free File Converter from Hosur" />
        <meta name="twitter:description" content="Built by Techb Software Solutions Pvt Ltd, Hosur — free file conversion for everyone." />
      </Helmet>

      {/* HERO */}
      <section className="hero">
        <h1>Powerful File Conversion <span>Made Simple</span></h1>
        <p>Built in Hosur, Tamil Nadu — for students, professionals, advocates and businesses worldwide.</p>
      </section>

      {/* ABOUT US */}
      <section className="section">
        <div className="container about-content">
          <h2 className="section-title">About Us</h2>
          <p>
            ILoveConvert is a free online file conversion platform built by{' '}
            <strong>Techb Software Solutions Pvt Ltd</strong>, based in Hosur, Tamil Nadu, India.
            Founded in 2026, our mission is simple — make file conversion fast, free,
            and accessible to everyone without requiring signup or software installation.
          </p>
          <p>
            Today, ILoveConvert offers <strong>free PDF and image tools</strong> — merge, split, rotate,
            image conversion, background removal, OCR and QR codes, serving students, professionals, advocates, and businesses
            across India and beyond. All tools are completely free, with no watermarks,
            no account required, and no file size tricks.
          </p>

          <h2>Our Story</h2>
          <p>
            The idea for ILoveConvert was born out of a real frustration. Most free file
            converter websites are slow, covered in ads, or force users to create accounts
            just to convert a single PDF. As developers who deal with file conversions daily,
            we decided to build a cleaner, faster alternative.
          </p>
          <p>
            What started as an internal tool at Techb Software Solutions Pvt Ltd in early 2026
            grew into ILoveConvert — a single place for the PDF and image conversions
            we found ourselves reaching for constantly, from document compression to
            AI-powered background removal, all free and all in one place.
          </p>

          <h2>Who We Are</h2>
          <p>
            ILoveConvert is built and maintained by <strong>Techb Software Solutions Pvt Ltd</strong>,
            a software development company based in Hosur, Tamil Nadu. Our team specialises
            in building practical web tools that solve everyday problems for real users.
          </p>
          <p>
            Our platform is powered by LibreOffice, Sharp, and pdf-lib — the same
            open-source engines trusted by enterprise document systems worldwide. Every
            file uploaded to ILoveConvert is processed securely over HTTPS on our own
            servers. The converted result is briefly stored via Cloudinary to keep your
            download link reliable, then automatically deleted within 2 hours — we never
            read or analyze your file contents, and we never use them to train anything.
          </p>
        </div>
      </section>

      {/* VISION AND MISSION */}
      <section className="section" style={{ background: 'var(--glass-1)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="container about-content">
          <h2 className="section-title">Our Vision & Mission</h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, marginBottom: 32 }}>
            <div style={{ background: 'var(--glass-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: 28 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700, marginBottom: 12, color: 'var(--primary)' }}>Our Vision</h3>
              <p style={{ color: 'var(--t-secondary)', lineHeight: 1.8, fontSize: '0.93rem' }}>
                To become India's most trusted and widely used free file conversion platform —
                empowering every student, professional, and business to handle documents
                effortlessly without paying for software or subscriptions.
              </p>
            </div>

            <div style={{ background: 'var(--glass-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: 28 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🚀</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700, marginBottom: 12, color: 'var(--primary)' }}>Our Mission</h3>
              <p style={{ color: 'var(--t-secondary)', lineHeight: 1.8, fontSize: '0.93rem' }}>
                To make file conversion fast, free, and accessible to everyone — without
                signup, without watermarks, and without hidden charges. We believe essential
                document tools should be available to everyone, regardless of budget or
                technical knowledge.
              </p>
            </div>
          </div>

          <p style={{ color: 'var(--t-secondary)', lineHeight: 1.8 }}>
            We believe that access to quality digital tools should not depend on your budget.
            Whether you are a Class 10 student scanning assignment photos, an advocate
            preparing court bundles, an HR professional sending offer letters, or a
            small business owner creating invoices — ILoveConvert is built for you.
          </p>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="section">
        <div className="container about-content">
          <h2 className="section-title">What We Offer</h2>
          <p>
            ILoveConvert provides a comprehensive set of file conversion tools covering
            every common document and image format need:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, margin: '24px 0' }}>
            {[
              { icon: '📄', title: 'PDF Tools', desc: 'Merge, split, and rotate PDF files, and turn images into PDFs. Handle everything from a single page to multi-document bundles.' },
              // Office conversions are switched off for now.
              // { icon: '📝', title: 'Document Conversion', desc: 'PDF to Word, Word to PDF, Excel to PDF, PowerPoint to PDF and more. Preserve formatting across all major document formats.' },
              { icon: '🔍', title: 'Image to Text (OCR)', desc: 'Pull editable text out of photos, screenshots, and scans so you can copy it instead of retyping it.' },
              { icon: '🖼️', title: 'Image Conversion', desc: 'Convert between JPG, PNG, WebP, GIF, BMP, AVIF, TIFF, ICO and PDF. Resize, compress and optimize images for any platform.' },
              { icon: '✂️', title: 'Background Removal', desc: 'AI-powered background removal for product photos, profile pictures, and design assets. Clean, transparent PNG output in seconds.' },
              { icon: '🔒', title: 'Secure Processing', desc: 'All files processed over HTTPS. Automatically deleted within 2 hours. No long-term storage. Never shared with third parties.' },
              { icon: '📱', title: 'Mobile Ready', desc: 'Fully responsive — works on any iPhone or Android device. Upload from camera roll, convert, and download directly on your phone.' },
            ].map(f => (
              <div key={f.title} style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: '20px 24px' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>{f.icon}</div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700, marginBottom: 8, color: 'var(--t-primary)' }}>{f.title}</h3>
                <p style={{ color: 'var(--t-secondary)', fontSize: '0.88rem', lineHeight: 1.7 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI FEATURES — "What's Live and What's Next" is hidden for now: it
          lists paused tools and "Coming 2027/2028" features, which read as
          an unfinished site during an AdSense review. Restore when the
          paused tools are back.
      <section className="section" style={{ background: 'linear-gradient(135deg, #0d1117, #1a1f35)' }}>
        <div className="container about-content">
          <h2 className="section-title" style={{ color: 'white' }}>What's Live and What's Next</h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, marginBottom: 24 }}>
            Some of what's below is genuinely AI-powered — like background removal, which runs a real
            segmentation model. Some is solid engineering rather than AI, and we'd rather tell you which
            is which than dress up a conversion pipeline as artificial intelligence. Here's what's live today
            and what is coming soon:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
            {[
              {
                icon: '🤖',
                title: 'AI Background Removal',
                status: '✅ Live',
                statusColor: '#22c55e',
                desc: 'Our background removal tool uses a deep learning model trained on millions of images. It accurately detects subjects — people, products, logos — and removes backgrounds in under 3 seconds with clean, precise edges.'
              },
              {
                icon: '🧠',
                title: 'Structural PDF Compression',
                status: '✅ Live',
                statusColor: '#22c55e',
                desc: 'Our PDF compressor repacks the document’s internal structure into compressed object streams — genuinely lossless, so text-heavy PDFs shrink with zero quality loss. It doesn’t re-encode images, so heavily scanned/image-based PDFs will compress less; for those, we’re honest that the fix is smaller source images, not a bigger compression setting.'
              },
              {
                icon: '📊',
                title: 'PDF to Excel, PDF to Word, Word/Excel to PDF',
                status: '⏸️ Temporarily unavailable',
                statusColor: '#f59e0b',
                desc: 'These Office-document conversions are temporarily paused while we move the underlying document engine to infrastructure that supports it properly — we\'d rather take a tool offline than let it fail silently. All other conversions are unaffected.'
              },
              {
                icon: '🔍',
                title: 'OCR Text Recognition',
                status: '✅ Live',
                statusColor: '#22c55e',
                desc: 'Optical Character Recognition (OCR) extracts real, selectable text from images, screenshots, and scans — try it on the Image to Text page. Currently optimised for English; additional language support may follow.'
              },
              {
                icon: '☁️',
                title: 'Cloud Integration',
                status: '🔄 Coming 2027',
                statusColor: '#60a5fa',
                desc: 'Direct upload from Google Drive and OneDrive — convert files without downloading them first. Save converted files back to cloud storage with one click. Enterprise-grade workflow automation planned for 2027.'
              },
              {
                icon: '⚡',
                title: 'AI Document Summarisation',
                status: '🔄 Coming 2028',
                statusColor: '#a855f7',
                desc: 'Upload a long PDF report and get a concise AI-generated summary with key points, dates, and action items extracted automatically. Built for professionals handling large volumes of documents daily.'
              },
            ].map(f => (
              <div key={f.title} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '20px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <span style={{ fontSize: 32 }}>{f.icon}</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: f.statusColor, background: 'rgba(0,0,0,0.3)', padding: '3px 10px', borderRadius: 20 }}>{f.status}</span>
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700, marginBottom: 8, color: 'white' }}>{f.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.87rem', lineHeight: 1.7 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* WHY CHOOSE ILOVECONVERT */}
      <section className="section">
        <div className="container about-content">
          <h2 className="section-title">Why Choose ILoveConvert?</h2>
          <p style={{ color: 'var(--t-secondary)', marginBottom: 24, lineHeight: 1.8 }}>
            There are dozens of file converter websites online. Here is what makes
            ILoveConvert different:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 32 }}>
            {[
              {
                icon: '🆓',
                title: 'Truly Free — No Tricks',
                desc: 'No "free tier" with hidden limits. No watermarks on output. No page count restrictions. No email capture. Convert as many files as you need, completely free, forever.'
              },
              {
                icon: '⚡',
                title: 'Fast — Under 10 Seconds',
                desc: 'Most conversions complete in under 10 seconds. No waiting in queues, no slow progress bars. Our backend is optimised for speed using LibreOffice, Sharp and pdf-lib.'
              },
              {
                icon: '🔒',
                title: 'Private & Secure',
                desc: 'Files are transferred over HTTPS encryption. Automatically deleted within 2 hours of upload. We never read, index, or share your documents with anyone.'
              },
              {
                icon: '🧩',
                title: 'No Account Required',
                desc: 'Start converting immediately — no signup form, no email verification, no login. An optional free account is available for users who want conversion history.'
              },
              {
                icon: '📱',
                title: 'Works on All Devices',
                desc: 'Fully responsive — tested on Android, iPhone, iPad, Windows, Mac, and Linux. Upload from your camera roll, convert, and download in under a minute from any device.'
              },
              {
                icon: '🇮🇳',
                title: 'Built for Indian Users',
                desc: 'Designed with Indian workflows in mind — Aadhaar/PAN document preparation, NSP scholarship submissions, eCourt filing, student assignment submissions, and GST invoice handling.'
              },
              {
                icon: '🖼️',
                title: 'All Common Image Formats',
                desc: 'JPG, PNG, WebP, GIF, BMP, TIFF, AVIF, ICO and PDF — convert between the image formats people actually use.'
              },
              {
                icon: '🛠️',
                title: 'Enterprise-Grade Engines',
                desc: 'Powered by LibreOffice (used by governments and enterprises worldwide), Sharp (Node.js image processing), and pdf-lib (reliable PDF manipulation). Not cheap web APIs.'
              },
            ].map(f => (
              <div key={f.title} style={{ display: 'flex', gap: 16, padding: '20px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 28, minWidth: 40, textAlign: 'center' }}>{f.icon}</div>
                <div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700, marginBottom: 6, color: 'var(--t-primary)' }}>{f.title}</h3>
                  <p style={{ color: 'var(--t-secondary)', fontSize: '0.88rem', lineHeight: 1.7 }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO USES ILOVECONVERT */}
      <section className="section" style={{ background: 'var(--glass-1)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="container about-content">
          <h2 className="section-title">Who Uses ILoveConvert?</h2>
          <p style={{ color: 'var(--t-secondary)', lineHeight: 1.8, marginBottom: 24 }}>
            ILoveConvert is used by a wide range of users across India for everyday document tasks:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {[
              { icon: '🎓', title: 'Students', desc: 'Convert handwritten assignment photos to PDF, prepare scholarship documents for NSP/CUET portals, merge marksheets and certificates.' },
              { icon: '💼', title: 'Professionals', desc: 'Create professional PDF reports, convert Excel data for clients, build digital business cards, format resumes for job applications.' },
              { icon: '⚖️', title: 'Advocates & Lawyers', desc: 'Merge case file bundles, compress evidence PDFs for eCourt filing, split large judgment downloads, secure confidential documents.' },
              { icon: '🏢', title: 'Small Businesses', desc: 'Convert invoices and quotations to PDF, compress product images for websites, prepare GST documents, create shareable business cards.' },
              { icon: '📸', title: 'Designers & Creators', desc: 'Convert between image formats, remove backgrounds for product photos, optimise images for web and social media platforms.' },
              { icon: '🏥', title: 'Healthcare & Admin', desc: 'Convert medical reports and prescriptions, prepare KYC documents, handle patient record digitisation, manage insurance claim submissions.' },
            ].map(f => (
              <div key={f.title} style={{ background: 'var(--glass-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: '20px 20px' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>{f.icon}</div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '0.95rem', fontWeight: 700, marginBottom: 8, color: 'var(--t-primary)' }}>{f.title}</h3>
                <p style={{ color: 'var(--t-secondary)', fontSize: '0.85rem', lineHeight: 1.7 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR VALUES */}
      <section className="section">
        <div className="container about-content">
          <h2 className="section-title">Our Values</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
            {[
              { icon: '🆓', title: 'Free Forever', desc: 'No hidden charges, no premium plans required. Core tools will always be free.' },
              { icon: '🔒', title: 'Privacy First', desc: 'Files deleted after 2 hours. Never stored, shared, or analysed.' },
              { icon: '⚡', title: 'Speed Focused', desc: 'Most conversions complete in under 10 seconds.' },
              { icon: '📱', title: 'Mobile Friendly', desc: 'Works on any device, any browser, anywhere.' },
              { icon: '🧩', title: 'No Signup', desc: 'Start converting instantly. No forms, no accounts.' },
              { icon: '🌍', title: 'Accessible', desc: 'Designed for all users regardless of technical knowledge.' },
            ].map(f => (
              <div key={f.title} style={{ textAlign: 'center', padding: '20px 16px', background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)' }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>{f.icon}</div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '0.95rem', fontWeight: 700, marginBottom: 6, color: 'var(--t-primary)' }}>{f.title}</h3>
                <p style={{ color: 'var(--t-secondary)', fontSize: '0.83rem', lineHeight: 1.6 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROADMAP — hidden: listed planned/removed features (compression, "50+ tools")
          that the site doesn't offer, which reads as low-value content to AdSense.
      <section className="section" style={{ background: 'var(--glass-1)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="container about-content">
          <h2 className="section-title">Our Roadmap</h2>
          <p style={{ color: 'var(--t-secondary)', lineHeight: 1.8, marginBottom: 24 }}>
            We are building ILoveConvert step by step with a long-term vision:
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {[
              { year: '2026', color: '#22c55e', status: 'Current', items: ['50+ PDF and image conversion tools', 'AI-powered background removal', 'Smart PDF compression', 'Mobile-optimised experience', 'No signup free access', '30+ tutorial blog posts'] },
              { year: '2027', color: '#f59e0b', status: 'Planned', items: ['Google Drive and OneDrive integration', 'OCR text recognition (English, Hindi, Kannada, Tamil)', 'Batch file conversion', 'API access for developers', 'PDF password protection and removal'] },
              { year: '2028', color: '#a855f7', status: 'Vision', items: ['AI document summarisation', 'Smart form filling from PDFs', 'Automated document workflows', 'Enterprise dashboard and team accounts', 'Mobile apps for iOS and Android'] },
            ].map(r => (
              <div key={r.year} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 80, textAlign: 'center' }}>
                  <div style={{ background: r.color, color: 'white', fontWeight: 800, borderRadius: 10, padding: '6px 14px', fontSize: '1rem', fontFamily: 'var(--font-display)' }}>{r.year}</div>
                  <div style={{ fontSize: '0.75rem', color: r.color, fontWeight: 600, marginTop: 4 }}>{r.status}</div>
                </div>
                <div style={{ background: 'var(--glass-2)', border: `1px solid ${r.color}30`, borderRadius: 'var(--r-xl)', padding: '16px 20px', flex: 1 }}>
                  <ul style={{ color: 'var(--t-secondary)', lineHeight: 1.9, paddingLeft: '1.2rem', fontSize: '0.9rem', margin: 0 }}>
                    {r.items.map(item => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* STATS */}
      <section className="section">
        <div className="container text-center">
          <h2 className="section-title">ILoveConvert by the Numbers</h2>
          <div className="about-features" style={{ marginTop: 24}}>
            {[
              { icon: '🗂️', title: '9', desc: 'Image formats supported' },
              { icon: '⚡', title: '<10 sec', desc: 'Average conversion time' },
              { icon: '🔒', title: '2 hours', desc: 'Auto file deletion' },
              { icon: '📄', title: '50 MB', desc: 'Max file size' },
              { icon: '🌍', title: '100%', desc: 'Free forever' },
              { icon: '📱', title: 'All devices', desc: 'Mobile, tablet, desktop' },
            ].map(s => (
              <div className="about-card" style={{color: 'black' }} key={s.title}>
                <div className="icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY FEATURES */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #1D3557, #005CFC 55%, #193A6D)' }}>
        <div className="container text-center">
          <h2 className="section-title" style={{ color: 'white' }}>Why Choose ILoveConvert?</h2>
          <div className="about-features">
            {[
              { icon: '⚡', title: 'Fast Conversion', desc: 'Convert files instantly without delay. Most jobs finish in under 10 seconds.' },
              { icon: '🔒', title: 'Secure & Private', desc: 'Your files are safe — encrypted in transit and automatically deleted after 2 hours.' },
              { icon: '📱', title: 'Works Everywhere', desc: 'Use on any phone, tablet, or computer. No app download or installation required.' },
              { icon: '🆓', title: 'Always Free', desc: 'No hidden fees, no watermarks, no file count limits. 100% free, forever.' },
            ].map(f => (
              <div className="about-card" key={f.title} style={{ color: 'white' }}>
                <div className="icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.8)' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section">
        <div className="container about-content">
          <h2 className="section-title">Contact Us</h2>
          <p style={{ color: 'var(--t-secondary)', lineHeight: 1.8 }}>
            We love hearing from our users — whether it is feedback, feature requests,
            a bug report, or just to say hello. Our team responds within 24–48 hours.
          </p>
          <ul style={{ color: 'var(--t-secondary)', lineHeight: 2, fontSize: '0.95rem', paddingLeft: '1.2rem' }}>
            <li>🏢 <strong>Company:</strong> Techb Software Solutions Pvt Ltd</li>
            <li>📍 <strong>Address:</strong> No. HIG-51-A, Ground Floor, Avalapally Hudco,
                Phase VI, Basthi, Hosur, Tamil Nadu 635109, India</li>
            <li>✉️ <strong>Email:</strong> <a href="mailto:support@iloveconvert.in">support@iloveconvert.in</a></li>
          </ul>
          <div style={{ marginTop: 24 }}>
            <Link to="/contact" className="btn btn-primary">Contact Us</Link>
          </div>
        </div>
      </section>

    </div>
  );
}